import { type NavigateFunction } from 'react-router-dom'

import { List } from 'immutable'

import {
  CENTER_OF_EXCELLENCE,
  TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC,
} from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/types'
import { TRIAGE_ENTRY_POINTS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  HEALTH_CHALLENGES_SECURE_BASE_ROUTE,
  NEURODIVERSITY_SECURE_BASE_ROUTE,
  PAGE_ROUTES,
  WOMENS_HEALTH_SECURE_BASE_ROUTE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { TREATMENT_OPTIONS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import {
  isHealthPlanActive,
  shouldNavigateToHPIPage,
} from '@lyrahealth-inc/shared-app-logic/src/features/healthPlan/utils'
import {
  DirectLinkSource,
  SEARCHER_TYPE_TO_MATCH_FOR,
} from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { Linking } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'

import * as appointmentDashboardConstants from './appointmentDashboardConstants'
import {
  areAllCustomerProgramsBHBExclusive,
  isAnyCustomerProgramBHBExclusive,
} from '../../../common/constants/healthPlanConstants'
import { TREATMENT_RECOMMENDATIONS_ROUTE } from '../../../common/constants/routingConstants'
import { deleteAppointmentById, getAppointmentsByUserId } from '../../../common/http/data/appointmentDashboard'
import { matchPaths } from '../../../common/utils/stringUtils'
import {
  getCustomerHealthPlansList,
  getHealthPlanExtensionEnabled,
  getProgramCoverageBreakdown,
} from '../../../data/customer/customerSelectors'
import * as MixpanelActions from '../../../data/mixpanel/index'
import { getTriageEntryPoint } from '../../../data/register/registerSelectors'
import {
  getIsUserEAPSessionCountExhausted,
  getIsUserInternational,
  getIsUserInternationalAndMinor,
  getShouldUserSeeInternationalConsentForDataCollection,
} from '../../../data/user/userSelectors'
import { getIsHealthPlanActive } from '../../healthPlan/data/healthPlanSelectors'
import {
  clearDirectBookingChildLyraId,
  clearDirectLinkIntent,
  clearDirectLinkSource,
  clearDirectLinkUtmSource,
  setIsCurrentSearchDirectPath,
  setMeetingFormat,
  setToolkitType,
} from '../../onboard/data/onboardDataActions'
import {
  getIsLatestMatches,
  getLatestUserFavoriteProviders,
  getOnboardDirectLinkSource,
  getOnboardIsCurrentSearchDirectPath,
} from '../../onboard/data/onboardSelectors'
import { updateTriageDemographics } from '../../onboard/data/triage-demographics/triage-demographics-actions'
import { getIsTriageDemographicChild } from '../../onboard/data/triage-demographics/triageDemographicsSelectors'
import {
  clearTreatmentOptions,
  setSelectedTreatmentOption,
} from '../../onboard/treatmentOptions/data/treatmentOptionsActions'
import { saveHealthPlanSelectionFromTriage } from '../../onboard/triageHealthPlan/data/triageHealthPlanActions'
import { setHighRiskModalLevelShown, setToolkitSubTopic } from '../../triage/data/triageActions'

export interface handleZeroViewSelectArgs {
  whoAmIHereFor: SEARCHER_TYPE_TO_MATCH_FOR
  navigate: NavigateFunction
  currentLocation: string
  showSummaryView: boolean
  toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
  toolkitType?: CENTER_OF_EXCELLENCE
}

/* ---------------SCHEDULE CONFIRMATION PAGE----------------- */

export function clearSelectedAppointment() {
  return function (dispatch: $TSFixMe) {
    dispatch({
      type: appointmentDashboardConstants.CLEAR_SELECTED_APPOINTMENT,
    })
  }
}

export function clearBookingStatus() {
  return function (dispatch: $TSFixMe) {
    dispatch({
      type: appointmentDashboardConstants.CLEAR_BOOKING_STATUS,
    })
  }
}

export function appointmentDataLoaded() {
  return function (dispatch: $TSFixMe) {
    dispatch({
      type: appointmentDashboardConstants.APPOINTMENT_DATA_LOADED,
    })
  }
}

export function getAppointments({ userId }: $TSFixMe) {
  return function (dispatch: $TSFixMe) {
    return getAppointmentsByUserId(userId).then((response) => {
      dispatch({
        type: appointmentDashboardConstants.SET_APPOINTMENT_DASHBOARD_VALUE,
        data: response.data,
      })
      return response
    })
  }
}

// `patientLyraId` overrides the appointment owner in the request path. Defaults to the logged-in
// user (the common member flow); DBT for Teens passes the teen's lyraId so a caregiver can cancel a
// session on the teen's behalf (LCDELIVERY-7302). The teen's id is the appointment's patient_id.
export function deleteAppointment(appointmentId: $TSFixMe, patientLyraId?: string) {
  return function (_dispatch: $TSFixMe, getState: $TSFixMe) {
    const userId = patientLyraId ?? getState().getIn(['user', 'id'])
    return deleteAppointmentById({ userId, appointmentId })
  }
}

// `patientLyraId` overrides the appointment owner in the ICS path. Defaults to the logged-in user;
// DBT for Teens passes the teen's lyraId so a caregiver can download the teen's session ICS
// (LCDELIVERY-7302). The teen's id is the appointment's patient_id.
export function getAppointmentIcs(appointmentId: { appointmentId: string | number }, patientLyraId?: string) {
  return function (dispatch: $TSFixMe, getState: $TSFixMe) {
    dispatch(
      MixpanelActions.trackEventWithObj({
        event: MixpanelActions.MIXPANEL_EVENTS.BUTTON_PRESS,
        action: MixpanelActions.MIXPANEL_ACTION.ADD_TO_CALENDAR,
      }),
    )
    const userId = patientLyraId ?? getState().getIn(['user', 'id'])
    Linking.openURL(`${window.location.origin}/services/v1/calendar/ics/${userId}/${appointmentId.appointmentId}`)
  }
}

export function handleZeroViewSelect({
  whoAmIHereFor,
  navigate,
  currentLocation,
  showSummaryView,
  toolkitSubTopic,
  toolkitType,
}: handleZeroViewSelectArgs) {
  return (dispatch: $TSFixMe, getState: $TSFixMe) => {
    dispatch(clearDirectPathState())
    // Unlike the other routes below, Manager Hub is a 1Lyra page, so it is never the current location when a
    // Homebase-routed search starts (e.g. after registration). Keep its origin so the triage exit returns to it.
    const isManagerHubOrigin = getOnboardDirectLinkSource(getState()) === DirectLinkSource.MANAGER_HUB
    if (
      !isManagerHubOrigin &&
      !matchPaths(currentLocation, [
        PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE,
        PAGE_ROUTES.FAMILY_CARE_OPTIONS,
        PAGE_ROUTES.SEARCH_CARE.BENEFITS,
        PAGE_ROUTES.CENTER_OF_EXCELLENCE.NEURODIVERSITY.LANDING_PAGE,
        NEURODIVERSITY_SECURE_BASE_ROUTE,
        PAGE_ROUTES.CENTER_OF_EXCELLENCE.NEURODIVERSITY.CARE_OPTIONS,
        PAGE_ROUTES.CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES.LANDING_PAGE,
        HEALTH_CHALLENGES_SECURE_BASE_ROUTE,
        PAGE_ROUTES.CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES.CARE_OPTIONS,
        PAGE_ROUTES.CENTER_OF_EXCELLENCE.WOMENS_HEALTH.LANDING_PAGE,
        WOMENS_HEALTH_SECURE_BASE_ROUTE,
        PAGE_ROUTES.CENTER_OF_EXCELLENCE.WOMENS_HEALTH.CARE_OPTIONS,
      ])
    ) {
      // Don't clear the direct link source for interim pages that may lead to other direct path searches.
      // See useNavigateToSearchForCare in mobile for matching logic
      dispatch(clearDirectLinkSource())
    }
    dispatch(setHighRiskModalLevelShown(undefined))
    dispatch(setMeetingFormat(null))
    if (toolkitType) {
      dispatch(setToolkitType(toolkitType))
    }
    if (toolkitType && toolkitSubTopic) {
      dispatch(setToolkitSubTopic(toolkitSubTopic))
    }
    dispatch(updateTriageDemographics('matchFor', whoAmIHereFor))
    dispatch(requestHealthPlanInfoAndGoToView(navigate, currentLocation, showSummaryView))
  }
}

export function clearDirectPathState() {
  return (dispatch: $TSFixMe) => {
    dispatch(clearTreatmentOptions())
    dispatch(setIsCurrentSearchDirectPath(false))
    dispatch(clearDirectLinkIntent())
    dispatch(clearDirectLinkUtmSource())
    dispatch(clearDirectBookingChildLyraId())
  }
}

const requestHealthPlanInfoAndGoToView = (
  navigate: NavigateFunction,
  currentLocation: string,
  showSummaryView: boolean,
) => {
  return (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState()
    const isHealthPlanActive = getIsHealthPlanActive(state)
    const customerPrograms = getProgramCoverageBreakdown(state)
    const isAllTreatmentOptionBHBExclusive = areAllCustomerProgramsBHBExclusive(customerPrograms)
    const isEAPSessionCountExhausted = getIsUserEAPSessionCountExhausted(state)
    const isMatchForChild = getIsTriageDemographicChild(state)
    const healthPlanList: List<$TSFixMe> = getCustomerHealthPlansList(state)
    const isUserInternational = getIsUserInternational(state)

    // When all options are HPI exclusive, an eligible health plan is required before searching for care
    // If the user does not have a confirmed health plan, navigate user to health plan form
    // However, if the user is international and health plan list is empty, go to treatment recs
    if (isAllTreatmentOptionBHBExclusive) {
      if (!isHealthPlanActive) {
        if (healthPlanList.size < 1 && isUserInternational) {
          // HEALTH_PLAN_FORM page will not load if customer has no health plans
          return navigate(TREATMENT_RECOMMENDATIONS_ROUTE)
        }
        return navigate(PAGE_ROUTES.HPI_COVERAGE_CHECK.ONBOARD.HEALTH_PLAN_FORM)
      }
    }

    // If any customer program supports HPI & EAP and user EAP sessions are exhausted, an eligible health
    // plan is required so we navigate the user there before search for care
    const healthPlanInfoRequired = shouldNavigateToHPIPage({
      customerPrograms: customerPrograms.toJS(),
      forChild: isMatchForChild,
      isHealthPlanActive,
      isEAPSessionCountExhausted,
    })
    if (healthPlanInfoRequired) {
      return navigate(PAGE_ROUTES.HPI_COVERAGE_CHECK.ONBOARD.HEALTH_PLAN_FORM)
    }

    dispatch(applySavedHealthPlanSelectionAndGoToOnboardingFlow(navigate, currentLocation, showSummaryView))
  }
}

export const applySavedHealthPlanSelectionAndGoToOnboardingFlow = (
  navigate: NavigateFunction,
  currentLocation: string,
  showSummaryView: boolean,
) => {
  return (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState()
    const healthPlanName = state.getIn(['healthPlan', 'healthPlanName'])
    const healthPlanList = getCustomerHealthPlansList(state)

    dispatch(applySavedHealthPlanSelection(healthPlanName, healthPlanList))
    return dispatch(goToOnboardingFlow(navigate, currentLocation, showSummaryView))
  }
}

// @ts-expect-error TS(7009): 'new' expression, whose target lacks a construct s... Remove this comment to see the full error message
const applySavedHealthPlanSelection = (healthPlanName: $TSFixMe, healthPlanList = new List()) => {
  return (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const customerPrograms = getProgramCoverageBreakdown(getState())
    const healthPlanEligibilityStatus = getState().getIn(['healthPlan', 'medicalPlanEligibilityStatus'], null)
    const isAnyTreatmentOptionBHBExclusive = isAnyCustomerProgramBHBExclusive(customerPrograms)
    const isHealthPlanEnabled = getHealthPlanExtensionEnabled(getState())
    // Save previously selected healthplan selection as this will affect care options filtering
    let selectedHealthPlan = healthPlanList.find(
      (healthPlan: $TSFixMe) => healthPlan.get('eligibility_name') === healthPlanName,
    )

    if (
      isAnyTreatmentOptionBHBExclusive &&
      isHealthPlanEnabled &&
      !isHealthPlanActive({
        healthPlanEligibilityStatus: healthPlanEligibilityStatus,
      })
    ) {
      selectedHealthPlan = { planName: 'unknown', bhbEligible: true }
    }
    if (selectedHealthPlan) {
      dispatch(saveHealthPlanSelectionFromTriage(selectedHealthPlan))
    }
  }
}

export const goToOnboardingFlow = (navigate: NavigateFunction, currentLocation: string, showSummaryView: boolean) => {
  return (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const whoAmIHereFor = getState().getIn(['triageDemographics', 'matchFor'], 'self')
    const shouldSeeInternationalConsentForDataCollection =
      getShouldUserSeeInternationalConsentForDataCollection(getState())
    const isUserInternationalAndMinor = getIsUserInternationalAndMinor(getState())
    const isLatestMatches = getIsLatestMatches(getState())
    const userFavoriteProviders = getLatestUserFavoriteProviders(getState())
    // get the last provider in the list as that is the latest provider
    const treatmentPreference = userFavoriteProviders[userFavoriteProviders.length - 1]?.treatmentPreference
    const isDirectPath = getOnboardIsCurrentSearchDirectPath(getState())

    switch (whoAmIHereFor) {
      case 'otherChild':
        navigate('/secure/onboard/about-child')
        break
      default:
        if (isDirectPath) {
          navigate('/secure/onboard/match-location')
        } else if (isUserInternationalAndMinor) {
          navigate(TREATMENT_RECOMMENDATIONS_ROUTE)
        } else if (shouldSeeInternationalConsentForDataCollection) {
          const currentEntryPoint = getTriageEntryPoint(getState())
          navigate('/secure/onboard/international-consent', {
            state: {
              returnEntryPoint:
                currentEntryPoint === TRIAGE_ENTRY_POINTS.CARE_TAB ? DirectLinkSource.CARE_TAB : undefined,
            },
          })
        } else if (isLatestMatches) {
          // Determine if we are coming from the saved provider results reengagement email
          const isComingFromSavedProviderResultsReengagementEmail = matchPaths(currentLocation, [
            PAGE_ROUTES.EMAILS_REDIRECT,
          ])

          dispatch(setSelectedTreatmentOption(treatmentPreference))
          if (treatmentPreference === TREATMENT_OPTIONS.COACHING) {
            navigate(
              {
                pathname: `/secure/onboard/${PAGE_ROUTES.COACHES}/${PAGE_ROUTES.LATEST_PROVIDER_MATCHES}`,
              },
              {
                state: {
                  isComingFromSavedProviderResultsReengagementEmail,
                  from: currentLocation,
                },
              },
            )
          } else {
            navigate(
              {
                pathname: `/secure/onboard/${PAGE_ROUTES.T1_MULTI_PROVIDERS}/${PAGE_ROUTES.LATEST_PROVIDER_MATCHES}`,
              },
              {
                state: {
                  isComingFromSavedProviderResultsReengagementEmail,
                  from: currentLocation,
                },
              },
            )
          }
        } else if (showSummaryView) {
          navigate(PAGE_ROUTES.ONBOARD.SUMMARY)
        } else {
          navigate('/secure/onboard/about', { state: { from: currentLocation } })
        }
    }
  }
}
